import { normalizeRoomCode } from '@calling/shared';
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccountNotice } from '../components/AccountNotice';
import { HostSteps, SignInFacts } from '../components/Education';
import { EmailSignIn } from '../components/EmailSignIn';
import { AppleLogo, GoogleLogo, MailIcon, MicrosoftLogo } from '../components/icons';
import { AgreeLine, Brand, LegalLinks, Page } from '../components/Shell';
import { Banner } from '../components/ui';
import { startSignIn } from '../lib/api';
import { describeAuthError } from '../lib/authErrors';
import { APP_NAME, platform } from '../lib/platform';
import { providerNames, usePasswordSignIn, useProviders, useServerInfo, type Provider } from '../lib/serverInfo';

// Pages that read what the server offers import it from here, with the sign-in parts.
export { useServerInfo };

/** The operator's help address as a link, or nothing when there is none. */
export function SupportLink() {
  const address = useServerInfo().info?.supportEmail;
  return address ? <a href={`mailto:${address}`}>{address}</a> : null;
}

/** The help address itself, for sentences that are shown only when there is one. */
export const useSupportEmail = (): string | null => useServerInfo().info?.supportEmail ?? null;

const LOGO = { google: GoogleLogo, microsoft: MicrosoftLogo, apple: AppleLogo };

/**
 * The buttons in the order they are shown: the server's (Google, Microsoft,
 * Apple), except in the iPhone and iPad app, where Apple's comes first.
 */
export function buttonOrder(providers: readonly Provider[], on: 'web' | 'android' | 'ios' = platform): Provider[] {
  return on === 'ios' ? [...providers.filter((p) => p.id === 'apple'), ...providers.filter((p) => p.id !== 'apple')] : [...providers];
}

/**
 * A sign-in button for each way this server offers. `returnTo` is where the
 * person lands afterwards. "Continue with email" opens the email and
 * password form in its place (EmailSignIn), with no browser; `onSignedIn`
 * replaces going to `returnTo` once that form has signed the person in.
 *
 * Apple's button follows Apple's design rules for a custom Sign in with
 * Apple button: the white style with a black logo and title, Apple's own
 * logo artwork at the button's full height, the system font at 43% of the
 * height, and a title that never changes (it only dims while busy). It is no
 * smaller than the others.
 */
export function ProviderButtons({ returnTo, onSignedIn }: { returnTo: string; onSignedIn?: () => void }) {
  const { providers, failed } = useProviders();
  const password = usePasswordSignIn();
  const [busy, setBusy] = useState<string | null>(null);
  const [withEmail, setWithEmail] = useState(false);
  if (failed) {
    return (
      <Banner tone="danger" title="Can't reach the server">
        Sign-in is unavailable right now. Check your connection and reload the page.
      </Banner>
    );
  }
  // A server that offers only an email address and a password shows its form straight away.
  if (withEmail || (providers?.length === 0 && password)) {
    return <EmailSignIn returnTo={returnTo} onSignedIn={onSignedIn} onBack={providers?.length ? () => setWithEmail(false) : undefined} autoFocus={withEmail} />;
  }
  if (providers && providers.length === 0 && password === false) {
    return (
      <Banner tone="danger" title="Sign-in is not set up yet">
        The operator of this service has not set up a way to sign in. Guests can still join meetings from a link.
      </Banner>
    );
  }
  const sandbox = providers?.some((p) => p.id === 'apple' && p.sandbox);
  return (
    <div className="stack">
      {buttonOrder(providers ?? []).map((p) => {
        const Logo = LOGO[p.id];
        // The iPhone's own Sign in with Apple sheet opens over the page, which stays: no wait afterwards.
        const sheet = p.id === 'apple' && platform === 'ios' && p.iosNative;
        return (
          <button
            key={p.id}
            className={`provider-btn${p.id === 'apple' ? ' provider-btn--apple' : ''}`}
            data-provider={p.id}
            disabled={busy !== null}
            onClick={() => {
              setBusy(p.id);
              // On the web this navigates away; in the app it opens the system browser, or Apple's sheet.
              startSignIn(p.id, returnTo, { iosNative: p.iosNative }).finally(() => (sheet ? setBusy(null) : window.setTimeout(() => setBusy(null), 4000)));
            }}
          >
            <Logo />
            {busy === p.id && p.id !== 'apple' ? `Opening ${p.label}…` : `Continue with ${p.label}`}
          </button>
        );
      })}
      {password ? (
        <button className="provider-btn" data-testid="continue-with-email" disabled={busy !== null} onClick={() => setWithEmail(true)}>
          <MailIcon />
          Continue with email
        </button>
      ) : null}
      {providers === null ? <div className="provider-btn" aria-hidden="true" style={{ opacity: 0.25 }} /> : null}
      {sandbox ? (
        <p className="muted" style={{ fontSize: 13, margin: 0 }} data-testid="apple-sandbox-line">
          On this computer, Apple sign-in is a stand-in (sandbox). It never contacts Apple, and you choose who to be.
        </p>
      ) : null}
      {/* Signing in, by any of the ways above, accepts the Terms. */}
      <AgreeLine action="continuing" />
    </div>
  );
}

export function JoinWithCode() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const code = normalizeRoomCode(value);
    if (!code) return setError('Enter the 10-letter code from your invitation, like abc-defg-hij, or paste the whole link.');
    navigate(`/m/${code}`);
  };
  return (
    <form className="field" onSubmit={submit} noValidate>
      <label htmlFor="code" className="muted" style={{ fontSize: 13, fontWeight: 400 }}>
        Join with a code
      </label>
      <div className="row" style={{ gap: 8 }}>
        <input
          id="code"
          className="input input--mono"
          style={{ height: 48 }}
          placeholder="abc-defg-hij"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          value={value}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'code-error' : undefined}
          onChange={(e) => {
            setValue(e.target.value);
            setError('');
          }}
        />
        <button className="btn btn--medium" style={{ height: 48, border: 'none', background: 'var(--surface-2)' }}>
          Join
        </button>
      </div>
      {error ? (
        <span className="error" id="code-error" role="alert">
          {error}
        </span>
      ) : null}
    </form>
  );
}

/** What went wrong with the last sign-in attempt, if anything. */
export function AuthErrorBanner({ authError, onDismissError }: { authError: string | null; onDismissError: () => void }) {
  const providers = useProviders().providers?.map((p) => p.label);
  const password = usePasswordSignIn() === true;
  const message = authError ? describeAuthError(authError, providers, { password }) : null;
  if (!message) return null;
  return (
    <Banner
      tone={authError === 'cancelled' ? 'info' : 'danger'}
      title={message.title}
      testId="auth-error"
      action={
        <button className="btn" onClick={onDismissError}>
          Dismiss
        </button>
      }
    >
      {message.body}
    </Banner>
  );
}

/**
 * Everything needed to sign in, or to join someone else's meeting without
 * an account. Shown on the sign-in page and on the landing page.
 */
export function SignInPanel({ returnTo }: { returnTo: string }) {
  const names = providerNames(useProviders().providers);
  const password = usePasswordSignIn();
  return (
    <>
      <p className="muted" style={{ fontSize: 15, lineHeight: 1.5 }}>
        {password && names
          ? `Sign in to host meetings, with the ${names} account you already have or with your email address and a password.`
          : password
            ? 'Sign in to host meetings with your email address and a password.'
            : `Sign in to host meetings. There is no new password to create: use ${names ? `the ${names} account` : 'an account'} you already have.`}
      </p>
      <ProviderButtons returnTo={returnTo} />
      {password && !names ? null : (
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {password
            ? `With ${names} we only receive your name and email address, and signing in for the first time creates your ${APP_NAME} account.`
            : `We only receive your name and email address. Signing in for the first time creates your ${APP_NAME} account.`}
        </p>
      )}
      <details className="disclosure">
        <summary>How sign-in works</summary>
        <SignInFacts />
      </details>
      <details className="disclosure">
        <summary>How hosting a meeting works</summary>
        <div style={{ paddingBottom: 14 }}>
          <HostSteps />
        </div>
      </details>
      <div className="card stack">
        <div className="stack stack--tight">
          <b style={{ fontSize: 15 }}>Joining someone else’s meeting?</b>
          <span className="muted" style={{ fontSize: 13 }}>
            You don’t need an account. Open the link you were sent, or enter its code.
          </span>
        </div>
        <JoinWithCode />
      </div>
    </>
  );
}

/** The sign-in page, shown wherever a page needs an account and nobody is signed in. */
export default function SignIn({ authError, onDismissError }: { authError: string | null; onDismissError: () => void }) {
  const location = useLocation();
  return (
    <Page>
      <header className="app-header">
        <Brand />
      </header>
      <AuthErrorBanner authError={authError} onDismissError={onDismissError} />
      {/* After deleting the account, or signing out everywhere: the apps open here. */}
      <AccountNotice />
      <h1 className="hero" style={{ marginTop: 8 }}>
        Start a call, share a link, admit who you want.
      </h1>
      {/* After signing in, the person lands on the page they were trying to open. */}
      <SignInPanel returnTo={`${location.pathname}${location.search}`} />
      <div className="spacer" />
      <LegalLinks />
    </Page>
  );
}
